/**
 * Black Mango — Awwwards-level Text Effects
 * TextSplitter, TextScramble, ScrollSkew, SmoothCounter
 * All respect prefers-reduced-motion.
 */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ========================================================================
     TextSplitter — [data-split-text]
     Splits text into individual character spans for char-reveal animations.
     ======================================================================== */
  class TextSplitter {
    constructor() {
      for (const el of document.querySelectorAll('[data-split-text]')) {
        this.#split(el);
      }
    }

    #split(el) {
      const text = el.textContent.trim();
      if (!text) return;

      el.setAttribute('aria-label', text);
      el.innerHTML = '';
      el.classList.add('char-reveal');

      const words = text.split(/\s+/);
      let charIndex = 0;

      for (let w = 0; w < words.length; w++) {
        if (w > 0) {
          const spacer = document.createElement('span');
          spacer.className = 'word-space';
          spacer.textContent = ' ';
          el.appendChild(spacer);
        }

        const wordSpan = document.createElement('span');
        wordSpan.className = 'word';

        for (const char of words[w]) {
          const charSpan = document.createElement('span');
          charSpan.className = 'char';
          charSpan.textContent = char;
          charSpan.style.setProperty('--char-index', charIndex);
          charSpan.setAttribute('aria-hidden', 'true');
          wordSpan.appendChild(charSpan);
          charIndex++;
        }

        el.appendChild(wordSpan);
      }

      /* Trigger reveal when element enters viewport */
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(el);
    }
  }

  /* ========================================================================
     TextScramble — [data-scramble]
     On mouseenter, scrambles text through random chars before resolving.
     ======================================================================== */
  class TextScramble {
    static #symbols = '!@#$%^&*()_+-=[]{}|;:,./<>?';
    static #numbers = '0123456789';
    static #letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

    constructor() {
      for (const el of document.querySelectorAll('[data-scramble]')) {
        const original = el.textContent;
        el.addEventListener('mouseenter', () => this.#scramble(el, original));
      }
    }

    #scramble(el, original) {
      if (el._scrambling) return;
      el._scrambling = true;

      const duration = 600;
      const frameRate = 30;
      const totalFrames = Math.ceil((duration / 1000) * frameRate);
      let frame = 0;

      const step = () => {
        frame++;
        const progress = frame / totalFrames;
        let result = '';

        for (let i = 0; i < original.length; i++) {
          if (original[i] === ' ') {
            result += ' ';
            continue;
          }

          const charProgress = Math.max(0, (progress - i / original.length / 2) * 2);

          if (charProgress >= 1) {
            result += original[i];
          } else if (charProgress > 0.6) {
            result += TextScramble.#letters[Math.floor(Math.random() * TextScramble.#letters.length)];
          } else if (charProgress > 0.3) {
            result += TextScramble.#numbers[Math.floor(Math.random() * TextScramble.#numbers.length)];
          } else {
            result += TextScramble.#symbols[Math.floor(Math.random() * TextScramble.#symbols.length)];
          }
        }

        el.textContent = result;

        if (frame < totalFrames) {
          requestAnimationFrame(step);
        } else {
          el.textContent = original;
          el._scrambling = false;
        }
      };

      requestAnimationFrame(step);
    }
  }

  /* ========================================================================
     ScrollSkew — [data-scroll-skew]
     Applies subtle skewY proportional to scroll velocity.
     ======================================================================== */
  class ScrollSkew {
    #elements = [];
    #lastScroll = 0;
    #velocity = 0;
    #rafId = null;
    #running = false;

    constructor() {
      this.#elements = [...document.querySelectorAll('[data-scroll-skew]')];
      if (!this.#elements.length) return;

      this.#lastScroll = window.scrollY;
      window.addEventListener('scroll', () => this.#onScroll(), { passive: true });
    }

    #onScroll() {
      const current = window.scrollY;
      this.#velocity = current - this.#lastScroll;
      this.#lastScroll = current;

      if (!this.#running) {
        this.#running = true;
        this.#rafId = requestAnimationFrame(() => this.#tick());
      }
    }

    #tick() {
      this.#velocity *= 0.9;

      const skew = Math.min(Math.max(this.#velocity * 0.08, -3), 3);
      const rounded = Math.round(skew * 100) / 100;

      if (Math.abs(rounded) > 0.01) {
        for (const el of this.#elements) {
          el.style.transform = `skewY(${rounded}deg)`;
        }
        this.#rafId = requestAnimationFrame(() => this.#tick());
      } else {
        for (const el of this.#elements) {
          el.style.transform = '';
        }
        this.#running = false;
      }
    }
  }

  /* ========================================================================
     SmoothCounter — [data-count-up]
     Counts from 0 to target number with easing when visible.
     ======================================================================== */
  class SmoothCounter {
    constructor() {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#animate(entry.target);
              observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.5 }
      );

      for (const el of document.querySelectorAll('[data-count-up]')) {
        observer.observe(el);
      }
    }

    #animate(el) {
      const target = parseFloat(el.dataset.countUp) || parseFloat(el.textContent) || 0;
      const decimals = (String(target).split('.')[1] || '').length;
      const prefix = el.dataset.countPrefix || '';
      const suffix = el.dataset.countSuffix || '';
      const duration = 1800;
      const start = performance.now();

      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        /* Ease-out cubic */
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * target;

        el.textContent = prefix + current.toFixed(decimals) + suffix;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.textContent = prefix + target.toFixed(decimals) + suffix;
        }
      };

      requestAnimationFrame(step);
    }
  }

  /* ========================================================================
     Initialize on DOMContentLoaded, respecting reduced motion
     ======================================================================== */
  document.addEventListener('DOMContentLoaded', () => {
    /* TextSplitter always initializes (layout is needed), but animations
       are disabled via CSS when reduced motion is preferred */
    new TextSplitter();

    if (!prefersReducedMotion.matches) {
      new TextScramble();
      new ScrollSkew();
      new SmoothCounter();
    }
  });
})();
